// src/pages/About.jsx
import React from "react";

export default function About() {
  React.useEffect(() => {
    document.title = "About Us";
  }, []);

  const [isSmallScreen, setIsSmallScreen] = React.useState(false);

  React.useEffect(() => {
    const mq = window.matchMedia("(max-width: 1024px)");
    const apply = () => setIsSmallScreen(mq.matches);
    apply();
    mq.addEventListener?.("change", apply);
    return () => mq.removeEventListener?.("change", apply);
  }, []);

  const pageStyle = {
    ...styles.page,
    ...(isSmallScreen ? styles.pageMobile : null),
  };

  const heroStyle = {
    ...styles.hero,
    ...(isSmallScreen ? styles.heroMobile : null),
  };

  const titleStyle = {
    ...styles.title,
    ...(isSmallScreen ? styles.titleMobile : null),
  };

  const twoColumnStyle = {
    ...styles.twoColumn,
    ...(isSmallScreen ? styles.stackGrid : null),
  };

  const problemGridStyle = {
    ...styles.problemGrid,
    ...(isSmallScreen ? styles.stackGrid : null),
  };

  const productGridStyle = {
    ...styles.productGrid,
    ...(isSmallScreen ? styles.productGridMobile : null),
  };

  const founderPanelStyle = {
    ...styles.founderPanel,
    ...(isSmallScreen ? styles.founderPanelMobile : null),
  };

  const comingNextStyle = {
    ...styles.comingNext,
    ...(isSmallScreen ? styles.comingNextMobile : null),
  };

  const footerGridStyle = {
    ...styles.footerGrid,
    ...(isSmallScreen ? styles.footerGridMobile : null),
  };

  return (
    <div style={pageStyle}>
      <section style={heroStyle}>
        <h1 style={titleStyle}>About AskLawly</h1>

        <div style={styles.badgeRow}>
          <Badge icon="▣" label="Privacy-first" />
          <Badge icon="◎" label="Multilingual" />
          <Badge icon="◇" label="Affordable access" />
          <Badge icon="⚖" label="Not a law firm" />
        </div>
      </section>

      <section style={twoColumnStyle}>
        <InfoCard
          icon="◎"
          title="Mission"
          text={
            <>
              Make advanced AI practical, safe, multilingual, and affordable — so everyday people
              can get clear answers and take confident action.
            </>
          }
          secondaryText="We combine AI with secure workflows to simplify complex legal and financial topics."
        />

        <InfoCard
          icon="◉"
          title="Vision"
          text={
            <>
              A world where high-quality AI assistance is trustworthy and accessible in every
              region, not just for those who can afford premium subscriptions.
            </>
          }
          secondaryText="We want AI help to feel as accessible as reading a clear, useful guide."
        />
      </section>

      <section style={founderPanelStyle}>
        <div style={styles.founderContent}>
          <h2 style={styles.sectionTitleNoMargin}>From Our Founder</h2>
          <p style={styles.bodyText}>
            I’m Imran Ahmed, a CPA, business owner, and longtime technology learner. AskLawly grew
            out of my own experience building dashboards, automation, and client workflows for my
            CPA firm after finding that many off-the-shelf tools did not fit real professional
            needs. I had earlier hands-on training in SQL Server, Unix/Linux systems, software
            testing, and programming concepts, and although my career moved into accounting and
            finance, I always stayed interested in building practical software. AskLawly brings
            those experiences together through privacy-focused AI tools that help people understand
            documents, organize information, ask better questions, and work more confidently with
            licensed professionals.
          </p>
          <div style={styles.founderName}>— Imran Ahmed, CPA</div>
          <div style={styles.founderSub}>Founder, NexGen Automate, Inc.</div>
        </div>
        {!isSmallScreen && <div style={styles.founderPattern} aria-hidden="true" />}
      </section>

      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>The Problem We’re Solving</h2>
        <div style={problemGridStyle}>
          <ProblemTile
            icon="?"
            title="Clarity"
            text="People face contradictory legal information and do not know what is reliable."
          />
          <ProblemTile
            icon="⌕"
            title="Transparency"
            text="Pricing often feels opaque, especially when users do not know what is reasonable."
          />
          <ProblemTile
            icon="◇"
            title="Access"
            text="High-quality help can require commitments many users cannot afford."
          />
          <ProblemTile
            icon="▣"
            title="Privacy"
            text="Users worry their information will be reused, marketed, or sold."
          />
        </div>
      </section>

      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>What AskLawly Offers</h2>
        <div style={productGridStyle}>
          <ProductTile
            icon="●"
            title="Chat"
            text="Session-based AI assistance in plain English."
          />
          <ProductTile
            icon="▤"
            title="Contract Insight"
            text="Contract review for key terms, risks, and red flags."
          />
          <ProductTile
            icon="✎"
            title="DocWriter"
            text="Generate drafts with controls for tone, audience, and language."
          />
          <ProductTile
            icon="◉"
            title="Vision Assist"
            text="Screen-share guidance and up to 30 pages per upload."
          />
          <ProductTile
            icon="▣"
            title="FileVault"
            text="Secure storage for documents and case files."
          />
          <ProductTile
            icon="◔"
            title="Smart Alert"
            text="SMS/email notifications and reminder workflows."
          />
          <ProductTile
            icon="▥"
            title="Case Estimator"
            text="Directional fee and cost guidance based on user inputs."
          />
          <ProductTile
            icon="$"
            title="Case Value"
            text="Directional view of potential case value scenarios."
          />
        </div>
      </section>

      <section style={styles.disclaimerBanner}>
        <span style={styles.warningIcon}>⚠</span>
        <span>
          <strong>AskLawly is not a law firm</strong> and does not provide legal services or legal
          advice. Information is for educational purposes only and not a substitute for advice from
          a licensed attorney.
        </span>
      </section>

      <section style={twoColumnStyle}>
        <InfoCard
          icon="$"
          title="Access & Affordability"
          variant="green"
          text={
            <>
              We’re committed to affordable access worldwide — including Africa, Asia, and Latin
              America.
            </>
          }
          secondaryText="Alongside subscriptions, we support session-based access so people can use advanced AI help without committing to a monthly plan."
        />

        <InfoCard
          icon="▣"
          title="Privacy You Can Trust"
          text={
            <>
              We are a privacy-focused company. We do not sell your personal information, email, or
              address.
            </>
          }
          secondaryText="We do not use your data for marketing. Your content stays yours."
        />
      </section>

      <section style={comingNextStyle}>
        <div>
          <h2 style={styles.sectionTitleNoMargin}>Coming Next</h2>
          <p style={styles.bodyText}>
            We are also exploring practical AI workflows for education, accounting and finance, and
            restaurant / quick-service operations.
          </p>
        </div>
        <div style={styles.comingList}>
          <NextItem title="Education (K–12)" text="Child-friendly, multilingual learning with live AI tutoring across 12 grades." />
          <NextItem title="Accounting & Finance" text="AI-assisted invoices, expenses, firm dashboards, and a client portal for CPA/accounting firms." />
          <NextItem title="Restaurant / Quick-Service" text="Voice + AI ordering for speed and accuracy." />
        </div>
      </section>

      <section style={styles.footerPanel}>
        <h2 style={styles.footerTitle}>Ownership & Contact</h2>
        <div style={footerGridStyle}>
          <FooterItem icon="▥" text={<><strong>NexGen Automate, Inc.</strong></>} />
          <FooterItem icon="◎" text={<><strong>AskLawly.com</strong></>} />
          <FooterItem icon="⌖" text="Wilmington, New Castle County, Delaware, USA" />
          <FooterItem icon="◌" text={<><strong>Founder:</strong> Imran Ahmed, CPA</>} />
          <FooterItem
            icon="✉"
            text={
              <a style={styles.link} href="mailto:support@asklawly.com">
                support@asklawly.com
              </a>
            }
          />
        </div>
        <div style={styles.footerLinks}>
          <a style={styles.link} href="/terms">Terms of Use</a>
          <span style={styles.footerDivider}>|</span>
          <a style={styles.link} href="/privacy">Privacy Policy</a>
        </div>
      </section>
    </div>
  );
}

const Badge = ({ icon, label }) => (
  <span style={styles.badge}>
    <span style={styles.badgeIcon}>{icon}</span>
    {label}
  </span>
);

const InfoCard = ({ icon, title, text, secondaryText, variant }) => {
  const isGreen = variant === "green";
  return (
    <article style={styles.infoCard}>
      <div
        style={{
          ...styles.largeIcon,
          ...(isGreen ? styles.largeIconGreen : null),
        }}
      >
        {icon}
      </div>
      <div>
        <h2 style={styles.cardTitle}>{title}</h2>
        <p style={styles.cardText}>{text}</p>
        {secondaryText && <p style={styles.cardText}>{secondaryText}</p>}
      </div>
    </article>
  );
};

const ProblemTile = ({ icon, title, text }) => (
  <article style={styles.problemTile}>
    <div style={styles.tileIcon}>{icon}</div>
    <div>
      <h3 style={styles.tileTitle}>{title}</h3>
      <p style={styles.tileText}>{text}</p>
    </div>
  </article>
);

const ProductTile = ({ icon, title, text }) => (
  <article style={styles.productTile}>
    <div style={styles.productIcon}>{icon}</div>
    <div>
      <h3 style={styles.tileTitle}>{title}</h3>
      <p style={styles.tileText}>{text}</p>
    </div>
  </article>
);

const NextItem = ({ title, text }) => (
  <div style={styles.nextItem}>
    <strong style={styles.nextTitle}>{title}</strong>
    <span style={styles.nextText}>{text}</span>
  </div>
);

const FooterItem = ({ icon, text }) => (
  <div style={styles.footerItem}>
    <span style={styles.footerIcon}>{icon}</span>
    <span>{text}</span>
  </div>
);

const styles = {
  page: {
    maxWidth: 1120,
    margin: "0 auto",
    padding: "18px 16px 44px",
    boxSizing: "border-box",
    background: "#F5F7FA",
    fontFamily: "sans-serif",
  },
  pageMobile: {
    padding: "14px 12px 34px",
  },

  hero: {
    textAlign: "center",
    padding: "12px 12px 18px",
  },
  heroMobile: {
    paddingTop: 10,
  },
  title: {
    margin: 0,
    color: "#0F172A",
    fontSize: 34,
    lineHeight: 1.08,
    fontWeight: 950,
    letterSpacing: "-0.9px",
  },
  titleMobile: {
    fontSize: 30,
    letterSpacing: "-0.65px",
  },
  badgeRow: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 10,
    marginTop: 16,
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 12px",
    borderRadius: 12,
    border: "1px solid rgba(37,99,235,0.18)",
    background: "#FFFFFF",
    color: "#1E2A78",
    fontSize: 13,
    fontWeight: 900,
    boxShadow: "0 8px 18px rgba(2,6,23,0.035)",
  },
  badgeIcon: {
    color: "#2563EB",
    fontWeight: 950,
  },

  twoColumn: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 14,
    marginTop: 14,
  },
  stackGrid: {
    gridTemplateColumns: "1fr",
  },
  infoCard: {
    display: "grid",
    gridTemplateColumns: "70px minmax(0, 1fr)",
    gap: 14,
    alignItems: "start",
    padding: 18,
    borderRadius: 18,
    border: "1px solid rgba(2,6,23,0.10)",
    background: "#FFFFFF",
    boxShadow: "0 14px 34px rgba(2,6,23,0.065)",
  },
  largeIcon: {
    width: 56,
    height: 56,
    borderRadius: 18,
    background: "rgba(37,99,235,0.10)",
    color: "#2563EB",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 27,
    fontWeight: 950,
  },
  largeIconGreen: {
    background: "rgba(16,185,129,0.12)",
    color: "#059669",
  },
  cardTitle: {
    margin: "0 0 8px",
    color: "#0F172A",
    fontSize: 18,
    lineHeight: 1.25,
    fontWeight: 950,
    letterSpacing: "-0.25px",
  },
  cardText: {
    margin: "0 0 9px",
    color: "#334155",
    fontSize: 14,
    lineHeight: 1.55,
    fontWeight: 650,
  },

  founderPanel: {
    position: "relative",
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 130px",
    gap: 14,
    alignItems: "start",
    marginTop: 14,
    padding: 20,
    borderRadius: 18,
    border: "1px solid rgba(37,99,235,0.14)",
    background:
      "linear-gradient(135deg, rgba(37,99,235,0.07), rgba(255,255,255,0.94) 44%, rgba(255,255,255,1))",
    boxShadow: "0 14px 34px rgba(2,6,23,0.065)",
    overflow: "hidden",
  },
  founderPanelMobile: {
    display: "block",
    padding: 16,
  },
  founderContent: {
    minWidth: 0,
  },
  sectionTitleNoMargin: {
    margin: 0,
    color: "#0F172A",
    fontSize: 20,
    lineHeight: 1.25,
    fontWeight: 950,
    letterSpacing: "-0.3px",
  },
  bodyText: {
    margin: "9px 0 0",
    color: "#334155",
    fontSize: 14,
    lineHeight: 1.6,
    fontWeight: 650,
  },
  founderName: {
    marginTop: 10,
    color: "#2563EB",
    fontSize: 14,
    fontWeight: 950,
  },
  founderSub: {
    marginTop: 2,
    color: "#64748B",
    fontSize: 13,
    fontWeight: 700,
  },
  founderPattern: {
    width: 120,
    height: 120,
    borderRadius: 20,
    opacity: 0.4,
    backgroundImage:
      "radial-gradient(circle, rgba(37,99,235,0.32) 1.4px, transparent 1.4px)",
    backgroundSize: "12px 12px",
  },

  section: {
    marginTop: 14,
  },
  sectionTitle: {
    margin: "0 0 10px",
    color: "#0F172A",
    fontSize: 20,
    lineHeight: 1.25,
    fontWeight: 950,
    letterSpacing: "-0.3px",
  },
  problemGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 12,
  },
  problemTile: {
    display: "grid",
    gridTemplateColumns: "48px minmax(0, 1fr)",
    gap: 11,
    alignItems: "center",
    minHeight: 86,
    padding: 13,
    borderRadius: 16,
    border: "1px solid rgba(2,6,23,0.10)",
    background: "#FFFFFF",
    boxShadow: "0 10px 26px rgba(2,6,23,0.052)",
  },
  tileIcon: {
    width: 42,
    height: 42,
    borderRadius: 14,
    background: "rgba(37,99,235,0.10)",
    color: "#2563EB",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 20,
    fontWeight: 950,
  },
  tileTitle: {
    margin: "0 0 4px",
    color: "#0F172A",
    fontSize: 14.5,
    lineHeight: 1.25,
    fontWeight: 950,
  },
  tileText: {
    margin: 0,
    color: "#475569",
    fontSize: 12.8,
    lineHeight: 1.42,
    fontWeight: 650,
  },
  productGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 10,
  },
  productGridMobile: {
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  },
  productTile: {
    display: "grid",
    gridTemplateColumns: "44px minmax(0, 1fr)",
    gap: 10,
    alignItems: "center",
    minHeight: 76,
    padding: 12,
    borderRadius: 15,
    border: "1px solid rgba(2,6,23,0.10)",
    background: "#FFFFFF",
    boxShadow: "0 9px 22px rgba(2,6,23,0.045)",
  },
  productIcon: {
    width: 38,
    height: 38,
    borderRadius: 13,
    background: "rgba(37,99,235,0.10)",
    color: "#2563EB",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 18,
    fontWeight: 950,
  },

  disclaimerBanner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    marginTop: 14,
    padding: "12px 14px",
    borderRadius: 14,
    border: "1px solid rgba(245,158,11,0.34)",
    background: "#FFFBEB",
    color: "#78350F",
    fontSize: 14,
    lineHeight: 1.45,
    fontWeight: 750,
    textAlign: "center",
  },
  warningIcon: {
    color: "#D97706",
    fontSize: 18,
    fontWeight: 950,
  },

  comingNext: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 0.9fr) minmax(0, 1.5fr)",
    gap: 14,
    alignItems: "start",
    marginTop: 14,
    padding: 18,
    borderRadius: 18,
    border: "1px solid rgba(2,6,23,0.10)",
    background: "#FFFFFF",
    boxShadow: "0 12px 30px rgba(2,6,23,0.055)",
  },
  comingNextMobile: {
    gridTemplateColumns: "1fr",
    padding: 16,
  },
  comingList: {
    display: "grid",
    gap: 10,
  },
  nextItem: {
    display: "grid",
    gap: 4,
    padding: "10px 12px",
    borderRadius: 13,
    background: "#F8FAFC",
    border: "1px solid rgba(2,6,23,0.08)",
  },
  nextTitle: {
    color: "#0F172A",
    fontSize: 13.5,
    fontWeight: 950,
  },
  nextText: {
    color: "#475569",
    fontSize: 12.8,
    lineHeight: 1.45,
    fontWeight: 650,
  },

  footerPanel: {
    marginTop: 14,
    padding: 18,
    borderRadius: 18,
    border: "1px solid rgba(2,6,23,0.10)",
    background: "#FFFFFF",
    boxShadow: "0 12px 30px rgba(2,6,23,0.055)",
    textAlign: "center",
  },
  footerTitle: {
    margin: "0 0 13px",
    color: "#0F172A",
    fontSize: 18,
    fontWeight: 950,
    letterSpacing: "-0.25px",
  },
  footerGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
    gap: 10,
    alignItems: "center",
  },
  footerGridMobile: {
    gridTemplateColumns: "1fr",
  },
  footerItem: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 7,
    minHeight: 34,
    color: "#334155",
    fontSize: 12.8,
    lineHeight: 1.35,
    fontWeight: 700,
  },
  footerIcon: {
    color: "#2563EB",
    fontSize: 15,
    fontWeight: 950,
  },
  footerLinks: {
    marginTop: 13,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 12,
    fontSize: 13.5,
    fontWeight: 850,
  },
  footerDivider: {
    color: "#CBD5E1",
  },
  link: {
    color: "#2563EB",
    fontWeight: 850,
    textDecoration: "none",
  },
};
